import { Account } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { useQuery } from '@tanstack/react-query';
import { ACCOUNT_QUERY_KEYS, ACCOUNT_STALE_TIME } from '../constants';

/**
 * Custom hook for fetching account data by trading system account ID.
 *
 * @param sourceId - The trading system account ID to fetch account data for
 * @returns React Query result object containing account data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: account, isLoading, error, refetch } = useAccountData('ACCOUNT-123');
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage />;
 * if (!account) return <NotFound />;
 *
 * return <AccountDetails account={account} />;
 * ```
 */
export const useAccountData = (sourceId?: string) => {
  const {
    dataStore: { accountStore },
  } = useStore();

  return useQuery<Account>({
    queryKey: ACCOUNT_QUERY_KEYS.DASHBOARD(sourceId || ''),
    queryFn: async () => {
      if (!sourceId) throw new Error('Source ID is required');

      const accountResponse =
        await accountStore.getByTradingSystemAccount(sourceId);

      if (!accountResponse) {
        throw new Error(`Account not found: ${sourceId}`);
      }

      return accountResponse;
    },
    staleTime: ACCOUNT_STALE_TIME,
    enabled: !!sourceId,
  });
};
